import {
  AfterViewInit,
  ApplicationRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  ViewChild,
  forwardRef,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import SignaturePad from 'signature_pad';

@Component({
  selector: 'app-signature-field',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './signature-field.component.html',
  styleUrl: './signature-field.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SignatureFieldComponent),
      multi: true,
    },
  ],
})
export class SignatureFieldComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  @ViewChild('canvas', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;

  @Input() minWidth = 1;
  @Input() canvasWidth = 400;
  @Input() canvasHeight = 200;

  private pad: SignaturePad | null = null;
  private value: string | null = null;
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
  private appRef = inject(ApplicationRef);

  ngAfterViewInit(): void {
    const canvas = this.canvasRef.nativeElement;
    this.resizeCanvas();

    this.pad = new SignaturePad(canvas, {
      minWidth: this.minWidth,
      backgroundColor: 'rgba(255,255,255,0)',
    });

    this.pad.addEventListener('endStroke', () => {
      this.value = this.pad!.isEmpty() ? '' : this.pad!.toDataURL();
      this.onChange(this.value);
      this.onTouched();
      this.appRef.tick();
    });

    if (this.value) {
      this.applyValue(this.value);
    }
  }

  ngOnDestroy(): void {
    this.pad?.off();
  }

  @HostListener('window:resize')
  onResize(): void {
    if (!this.pad) return;
    const data = this.pad.toData();
    this.resizeCanvas();
    this.pad.clear();
    if (data?.length) {
      this.pad.fromData(data);
    } else if (this.value) {
      this.applyValue(this.value);
    }
  }

  writeValue(value: any): void {
    this.value = value || null;
    if (this.pad) {
      if (!value) {
        this.pad.clear();
      } else if (typeof value === 'string') {
        this.applyValue(value);
      }
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  clear(): void {
    this.pad?.clear();
    this.value = '';
    this.onChange('');
  }

  private applyValue(value: string): void {
    if (!this.pad) return;
    if (value.startsWith('http')) {
      fetch(value)
        .then((res) => res.blob())
        .then((blob) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const dataUrl = reader.result as string;
            this.pad?.fromDataURL(dataUrl);
            this.value = dataUrl;
          };
          reader.readAsDataURL(blob);
        });
    } else {
      this.pad.fromDataURL(value);
    }
  }

  private resizeCanvas(): void {
    const canvas = this.canvasRef.nativeElement;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const parent = canvas.parentElement;
    const width = parent ? Math.min(parent.clientWidth, this.canvasWidth) : this.canvasWidth;
    canvas.width = width * ratio;
    canvas.height = this.canvasHeight * ratio;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${this.canvasHeight}px`;
    const ctx = canvas.getContext('2d');
    ctx?.scale(ratio, ratio);
  }
}
